import { QuestionType } from './type/question.type';
import { get, filter, difference, keys, orderBy, isEqual } from 'lodash';
import { v4 as uuid } from 'uuid';
import { Option } from '../option';
import { SetOptions } from '@schoolbelle/common/object-change-tracker';
import { Survey } from '../survey';
import { OptionDataInterface } from '../option/base/optionData.interface';
import { startWith, map, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
export class Question extends QuestionType {
  subscriptions: Subscription[] = [];
  constructor(
    survey: Survey,
    question_id: string,
    question_type: string = undefined
  ) {
    super(survey, question_id, question_type);
    this.subscriptions.push(
      this.getChildTracker(['options'])
        .onContentUpdate.pipe(
          startWith(null),
          map(() => keys(this.get('options') || {})),
          distinctUntilChanged(
            (a: string[], b: string[]) => difference(a, b).length === 0
          )
        )
        .subscribe(() => this.refreshOptions())
    );
    // this.refreshOptions();
  }
  getNumbering(): number[] {
    let arr = [];
    let count = 1;
    let questions = this.survey.questions;
    let dependencyId = get(this, 'dependency.id');
    // if this question does not depend on any other question.
    if (!dependencyId || !this.survey.getQuestions({ id: dependencyId })[0]) {
      questions.forEach(q => {
        if (!q.dependency.id && q.priority < this.priority) count++;
      });
      arr.push(count);
      return arr;
    }
    // if this question DOES depend on any other question.
    else {
      questions.forEach(q => {
        if (
          get(q, 'dependency.id') === dependencyId &&
          q.priority < this.priority
        )
          count++;
      });
      let parentQuestion: Question = this.survey.getQuestions({
        id: dependencyId
      })[0];
      if (parentQuestion) return parentQuestion.getNumbering().concat([count]);
      else throw "couldn't locate parent question.";
    }
  }
  getOptions(conditions: any): Option[] {
    return filter(this.options, conditions);
  }
  addOption(
    oData: Partial<OptionDataInterface> = {},
    setOptions: SetOptions = undefined
  ): Option {
    let options = this.options;
    if (options.length > 100) throw 'options over 100 are not allowed';

    let option_id = oData.id || uuid();
    let option = (this._options[option_id] = new Option(
      this,
      option_id,
      oData.type
    ));
    option.load({ ...option.value, ...oData }, { push: 'none' });
    this.notifyChangeThruTree([], setOptions);
    return option;
  }
  deleteOptions(conditions: any, setOptions: SetOptions = undefined) {
    let count = 0;
    this.getOptions(conditions)
      .reverse()
      .forEach(option => {
        let option_id = option.id,
          priority = option.priority;
        if (typeof option_id === 'undefined') return;

        option.destroyTracker();
        delete this._options[option_id];
        this.options.filter(o => o.priority > priority).forEach(o => {
          o.set(['priority'], o.priority - 1, { push: 'none' });
        });
        this.set(['options', option_id], null, { push: 'none' });
        count++;
      });
    if (count) this.notifyChangeThruTree([], setOptions);
  }
  move(pos_n: number, withinRange = true): void {
    let question = this;
    let pos_o = question.priority;

    // 1. adjust to fit the range
    if (withinRange) {
      let [min, max] = this.getMovableRange();
      if (pos_n < min) pos_n = min;
      if (pos_n > max) pos_n = max;
    }

    // 2. adjust to treate a lock group of questions as one question
    let depth = question.getNumbering().length;
    let QuestionToBePushed: Question = question.survey.getQuestions({
      priority: pos_n
    })[0];
    if (QuestionToBePushed) {
      let sameLevelAncestorOfQuestionToBePushed = QuestionToBePushed.getAncestorQuestion(
        depth
      );
      if (sameLevelAncestorOfQuestionToBePushed) {
        let pushedQInstIsNthDecendant =
          QuestionToBePushed.priority -
          sameLevelAncestorOfQuestionToBePushed.priority;
        if (pos_o < pos_n) {
          // move down
          pos_n +=
            sameLevelAncestorOfQuestionToBePushed.getDecendentQuestionCnt() -
            pushedQInstIsNthDecendant;
        } else if (pos_o > pos_n) {
          // move up
          pos_n -= pushedQInstIsNthDecendant;
        }
      }
    }

    if (pos_o === pos_n) return;
    // move questionService and decendant questionServices altogether.
    const decendantQuestionList = this.survey.getQuestions({
      dependency: { id: this.id }
    });
    if (pos_o > pos_n) {
      orderBy(decendantQuestionList, 'priority', 'desc').forEach(
        decendantQuestion => decendantQuestion.move(pos_n, false)
      );
      this.priority = pos_n;
    } else {
      this.priority = pos_n;
      orderBy(decendantQuestionList, 'priority', 'asc').forEach(
        decendantQuestion => decendantQuestion.move(pos_n, false)
      );
    }

    // 3. set priority
    // this.priority = pos_n;
  }
  getDecendantQuestions(): Question[] {
    let question = this;
    let decendentQuestionCnt = question.getDecendentQuestionCnt();
    return question.survey.getQuestions((siblingQuestion: Question) => {
      return (
        question.priority < siblingQuestion.priority &&
        siblingQuestion.priority <= question.priority + decendentQuestionCnt
      );
    });
  }
  private getMovableRange(): [number, number] {
    let question = this;
    let min = question.priority,
      max = question.priority;
    let questionNumbering = question.getNumbering();

    question.survey.questions.forEach(siblingQuestion => {
      let siblingQuestionNumbering = siblingQuestion.getNumbering();
      if (
        siblingQuestionNumbering.length === questionNumbering.length &&
        isEqual(
          questionNumbering.slice(0, -1),
          siblingQuestionNumbering.slice(0, -1)
        )
      ) {
        if (siblingQuestion.priority < min) min = siblingQuestion.priority;
        if (siblingQuestion.priority > max) max = siblingQuestion.priority;
      }
    });
    return [min, max];
  }
  private getAncestorQuestion(wantedDepth: number = 1): false | Question {
    let question = this;

    let depth = question.getNumbering().length;
    if (depth < wantedDepth) {
      return false;
    } else if (depth === wantedDepth) {
      return question;
    } else {
      let parentQuestion: Question = question.survey.getQuestions({
        id: question.dependency.id
      })[0];
      if (!parentQuestion) {
        throw 'Parent questionService cannot be found!';
      }
      return parentQuestion.getAncestorQuestion(wantedDepth);
    }
  }
  private getDecendentQuestionCnt(cnt = 0): number {
    let question = this;
    let subQuestionList: Question[] = question.survey.getQuestions({
      dependency: { id: question.id }
    });
    cnt += subQuestionList.length;
    subQuestionList.forEach((subQuestion: Question) => {
      cnt = subQuestion.getDecendentQuestionCnt(cnt);
    });
    return cnt;
  }
  private refreshOptions() {
    let option_ids_new = keys(this.get(['options'])),
      option_ids_old = keys(this._options);
    difference(option_ids_new, option_ids_old).forEach(option_id => {
      this._options[option_id] = new Option(
        this,
        option_id,
        this.get(['options', option_id, 'type'])
      );
    });
    difference(option_ids_old, option_ids_new).forEach(option_id => {
      let option = this._options[option_id];
      if (option) {
        option.destroyTracker();
        option.set([], null);
        delete this._options[option_id];
      }
    });
  }
}
